import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import useAppointments from 'state/appointments';
import {
    DARKER_TEXT,
    DARK_TEXT,
    WARNING_ORANGE,
    BRAND_GREEN_PRIMARY,
} from 'resources/constants/colors';
import { getDisplayDate } from 'utils/date';
import { getDisplayTime } from 'utils/time';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled.div`
    animation: ${fadeUp} 0.35s 0.05s ease both;
`;

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 8px;
    padding: 0 2px;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
`;

const TaskItem = styled(Link)`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    text-decoration: none;
    border-bottom: 1px solid #f2f2f2;

    &:last-child { border-bottom: none; }
    &:active { opacity: 0.7; }
`;

const TaskIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    background: ${WARNING_ORANGE}18;
`;

const TaskInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const TaskTitle = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TaskSub = styled.p`
    font-size: 11px;
    color: ${DARK_TEXT};
    margin: 0;
`;

const Arrow = styled.span`
    font-size: 16px;
    color: #ccc;
    flex-shrink: 0;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 16px 10px;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: ${DARK_TEXT};
    margin: 4px 0 0;
`;

const Badge = styled.span`
    background: ${WARNING_ORANGE};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    border-radius: 12px;
    padding: 2px 8px;
    margin-left: 6px;
`;

const PendingTasks = () => {
    const [appointments, appointmentsDispatcher] = useAppointments();
    const list = appointments.pendingNotesList || [];

    useEffect(() => {
        appointmentsDispatcher.fetchPendingNotesStart();
    }, []);

    return (
        <Section>
            <SectionLabel>
                Notas pendientes
                {list.length > 0 && <Badge>{list.length}</Badge>}
            </SectionLabel>
            <Card>
                {list.length === 0 ? (
                    <EmptyState>
                        <span style={{ fontSize: 28 }}>✅</span>
                        <EmptyText>Todas las notas al día</EmptyText>
                    </EmptyState>
                ) : (
                    list.map((item) => (
                        <TaskItem
                            key={item.id || item.appointmentId}
                            to={`/appointment/${item.roomId}`}
                        >
                            <TaskIcon>📝</TaskIcon>
                            <TaskInfo>
                                <TaskTitle>
                                    {item.clientName || `${item.name || ''} ${item.lastName || ''}`.trim() || 'Paciente'}
                                </TaskTitle>
                                <TaskSub>
                                    {getDisplayDate(item.date, 'EEEE d MMM')} · {getDisplayTime(item.date)}
                                </TaskSub>
                            </TaskInfo>
                            <Arrow>›</Arrow>
                        </TaskItem>
                    ))
                )}
            </Card>
        </Section>
    );
};

export default PendingTasks;
